import type {
  CleaningCompletionReportResponse,
  CleaningCompletionReportTotals,
} from '@/shared/api';
import { formatNumber } from '@/shared/lib/format';

export function monthRange(day: string): { from: string; to: string } {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const prefix = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
  return { from: `${prefix}-01`, to: `${prefix}-${String(lastDay).padStart(2, '0')}` };
}

const MONTH = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DAY = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** „Oktober 2026“ für einen Monat, „1.10.2026 – 15.10.2026“ für einen freien Zeitraum. */
export function periodLabel(from: string, to: string, period: 'month' | 'range'): string {
  const day = (iso: string) => new Date(`${iso}T00:00:00Z`);
  return period === 'month'
    ? MONTH.format(day(from))
    : `${DAY.format(day(from))} – ${DAY.format(day(to))}`;
}

export function describeQuality(totals: CleaningCompletionReportTotals): string[] {
  const values: [number, string][] = [
    [totals.knownCount, 'vollständig'],
    [totals.partialCount, 'unvollständig'],
    [totals.unknownCount, 'unbekannt'],
    [totals.notApplicableCount, 'nicht anwendbar'],
  ];
  return values.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
}

/**
 * Gäste ohne erfundene Null: der Server zählt nur erfasste Abschluss-Stände. Fehlt bei allen die
 * Belegung, heißt die Zahl „unbekannt“; fehlt sie bei einigen, steht das dabei.
 */
export function guestsLabel(totals: CleaningCompletionReportTotals): string {
  const counted = totals.knownCount + totals.partialCount;
  if (counted === 0) return totals.unknownCount > 0 ? 'unbekannt' : 'keine';
  const value = formatNumber(totals.guests);
  return totals.unknownCount > 0 ? `${value} · ${totals.unknownCount} unbekannt` : value;
}

export function evidenceLabel(totals: CleaningCompletionReportTotals): string {
  if (totals.evidenceRequiredCount === 0) return 'nicht verlangt';
  return `${totals.evidenceCompleteCount} von ${totals.evidenceRequiredCount} vollständig`;
}

export function exportCompletionReportCsv(
  report: CleaningCompletionReportResponse,
  from: string,
  to: string,
) {
  const header = [
    'Objekt',
    'Apartment',
    'Reinigungen',
    'Gäste',
    'Fotonachweise',
    'Meldungen offen',
  ];
  const rows = report.apartments.map((apartment) => [
    apartment.objectName,
    apartment.operationalName ?? apartment.name,
    String(apartment.totals.cleaningCount),
    guestsLabel(apartment.totals),
    evidenceLabel(apartment.totals),
    String(apartment.totals.openIssueCount),
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((value) => `"${value.replaceAll('"', '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `reinigungsauswertung-${from}-bis-${to}.csv`;
  anchor.click();
  // Sofort freigegeben bricht mancher Browser den Download still ab.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
